/**
 * Seite "Kosten" eines Antrags im Modul Genehmigung.
 *
 * Zeigt die Kostenpositionen des Antrags. Über alle Anträge ergeben die beiden Summen darüber
 * den abgeleiteten CAPEX-Abschnitt "Genehmigung" in der Kalkulation.
 *
 * Nachweisgebundene Positionen entstehen weiterhin still im Nachweis-Detail; hier angelegt
 * werden nur Positionen ohne Dokumentbezug, etwa Behördengebühren.
 *
 * Erreichbar unter `/project/{projectId}/permits/{procedureId}/costs`.
 */
import React, { useEffect } from "react";
import { ProjectDetailPageProps } from "../../../types";
import { useAntragNavigation, useEditMode } from "../../layout/ProjectLayout";
import PageTemplate from "../../layout/PageTemplate";
import CostItemsSection from "./sections/CostItemsSection";
import { useProjectPermitCosts } from "./hooks/useProjectPermitCosts";
import { useToast } from "../../../contexts/ToastContext";

interface PermitCostsPageProps extends ProjectDetailPageProps {
  procedureId: string;
  canView: boolean;
  canEdit: boolean;
}

const PermitCostsPage: React.FC<PermitCostsPageProps> = ({
  project,
  procedureId,
  canEdit,
}) => {
  const { isEditMode } = useEditMode();
  const { oeffneAntrag } = useAntragNavigation();
  const { showError } = useToast();

  const {
    costItems,
    antrag,
    costSummary,
    loading,
    error,
    createCostItem,
    updateCostItem,
    deleteCostItem,
    isSaving,
    saveError,
  } = useProjectPermitCosts({ projectId: project?.id, procedureId });

  const editable = canEdit && isEditMode;

  const melde = (err: unknown, fallback: string) =>
    showError(err instanceof Error ? err.message : fallback);

  const handleCreate = async (
    permitProcedureId: string,
    data: Parameters<typeof createCostItem>[1],
  ) => {
    try {
      await createCostItem(permitProcedureId, data);
    } catch (err) {
      melde(err, "Kostenposition konnte nicht angelegt werden");
    }
  };

  const handleDelete = async (costItemId: string) => {
    try {
      await deleteCostItem(costItemId);
    } catch (err) {
      melde(err, "Kostenposition konnte nicht gelöscht werden");
    }
  };

  // Ein gelöschter oder fremder Antrag in der URL führt zur Liste statt ins Leere.
  const antragFehlt = !loading && !error && !antrag;
  useEffect(() => {
    if (antragFehlt) oeffneAntrag(null, undefined, { replace: true });
  }, [antragFehlt, oeffneAntrag]);

  return (
    <PageTemplate
      isLoading={loading}
      error={error}
      isSaving={isSaving}
      saveError={saveError}
    >
      <CostItemsSection
        costItems={costItems}
        costSummary={costSummary}
        // Nur der eine Antrag: „Position" fragt dann nicht erst nach dem Antrag.
        procedures={antrag ? [antrag] : []}
        canEdit={editable}
        onCreate={handleCreate}
        onUpdate={updateCostItem}
        onDelete={handleDelete}
      />
    </PageTemplate>
  );
};

export default PermitCostsPage;
